import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import cx from 'classnames';

import { Header } from '../components/Header';
import { PageMetadata } from '../components/PageMetadata';
import { Footer } from '../components/Footer/Footer';
import { Section, Row, Column } from '../components/Section';
import { Text } from '../components/Text';
import { CallToActionSection } from '../components/CallToActionSection';

import { GetServerPageProps } from '../lib/apollo';
import {
  getServerPageMedicare,
  getServerPageMedicareAbcdSection,
  getServerPageMedicareArticlesSection,
  getServerPageMedicareHeroSection,
  getServerPageMedicareLogosSection,
  getServerPageMedicarePathsSection,
  getServerPageMedicarePlansFootnotes,
  getServerPageMedicareQuestionsSection,
  getServerPageMedicareSupplementSection,
  getServerPageMedicareWhySection,
} from './Medicare.generated';
import { CommonQuestions } from '../components/CommonQuestions/CommonQuestions';
import { Hero } from '../components/Hero';
import { heroData } from '../components/heroData';
import { ProductLogos } from '../components/ProductLogos';
import { FeaturedArticles } from '../components/FeaturedArticles';
import { WhySection } from '../components/WhySection';
import { RichText } from '../lib/copyRendering';

export default function Medicare({
  pageMetadata,
  heroData,
  medicareHeroData,
  logosSection,
  whySection,
  abcdSection,
  pathsSection,
  supplementSection,
  commonQuestionsSection,
  articlesSection,
  footnotesSection,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  if (!pageMetadata) {
    throw new Error('Unable to load page');
  }
  return (
    <>
      <PageMetadata
        pageTitle={pageMetadata.seoTitle ?? undefined}
        description={pageMetadata.seoDescription ?? undefined}
        openGraphImage={pageMetadata.openGraphImage?.url ?? undefined}
      />
      <Header planType="medicare" />
      <Hero
        heading={
          medicareHeroData.snippetCollection?.items[0]?.name ?? heroData.heading
        }
        subheading={heroData.subheading}
        image={{
          url: pageMetadata.heroImage?.url ?? '',
          description: pageMetadata.heroImage?.description ?? '',
        }}
        aggregatedReviews={heroData.aggregatedReviews}
        defaultPlanTypeOption="medicare"
      />
      <WhySection
        snippetGroup={whySection.snippetGroupCollection?.items[0] ?? undefined}
        analyticsPageName="Medicare"
      />
      <CallToActionSection
        planType="medicare"
        quoteAction
        quoteActionLabel="See Medicare Plans"
      />
      <Section background="BLUE">
        <Row inset={true}>
          <Column className="text-left sm:text-center space-y-3">
            <Text.H2>
              {abcdSection.snippetGroupCollection?.items[0]?.name}
            </Text.H2>
            <Text.Body>
              {abcdSection.snippetGroupCollection?.items[0]?.subtitle}
            </Text.Body>
          </Column>
        </Row>
        <Row inset>
          <Column className="mt-8 space-y-8">
            {abcdSection.snippetGroupCollection?.items[0]?.snippetsCollection?.items.map(
              (snippet) =>
                snippet?.__typename === 'Snippet' && (
                  <div
                    className="flex space-x-12 items-start"
                    key={snippet.key}
                  >
                    {snippet.image?.url &&
                      snippet.image?.width &&
                      snippet.image?.height && (
                        <div className="flex-shrink-0 hidden sm:flex justify-center items-center mt-2">
                          <Image
                            src={snippet.image.url}
                            width={snippet.image.width}
                            height={snippet.image.height}
                            alt={snippet.image.description ?? ''}
                          />
                        </div>
                      )}
                    <div className="space-y-3">
                      <Text.H3>{snippet.name}</Text.H3>
                      <RichText content={snippet.content} />
                    </div>
                  </div>
                ),
            )}
          </Column>
        </Row>
      </Section>
      <Section>
        <Row inset={true}>
          <Column className="text-left sm:text-center space-y-3">
            <Text.H2>
              {pathsSection.snippetGroupCollection?.items[0]?.name}
            </Text.H2>
            <Text.Body>
              {pathsSection.snippetGroupCollection?.items[0]?.subtitle}
            </Text.Body>
          </Column>
        </Row>
        <Row inset>
          <Column className="mt-8 md:grid grid-cols-[1fr,min-content,1fr] gap-8">
            {pathsSection.snippetGroupCollection?.items[0]?.snippetsCollection?.items.map(
              (snippet, index) =>
                snippet?.__typename === 'Snippet' && (
                  <>
                    {index !== 0 && (
                      <div className="uppercase text-gray-900 font-bold text-2xl !mb-16 md:!mb-0 text-center">
                        or
                      </div>
                    )}
                    <div
                      className={cx(
                        'relative rounded-lg h-full flex flex-col text-center bg-white drop-shadow-lg border border-gray-400',
                        { '!mt-0': index === 1 },
                      )}
                      key={snippet.key}
                    >
                      <div className="absolute left-2/4 -translate-x-2/4 -translate-y-3/4 bg-indigo-700 px-8 py-4">
                        <Text.H4 className="text-white !font-bold">
                          {snippet.subtitle}
                        </Text.H4>
                      </div>
                      <div className="rounded-t-lg border-b border-gray-200 bg-white px-4 py-8 sm:px-6">
                        <Text.H3 className="!font-bold">{snippet.name}</Text.H3>
                      </div>

                      <div className="mx-4 my-8">
                        <RichText content={snippet.content} />
                      </div>
                    </div>
                  </>
                ),
            )}
          </Column>
        </Row>
      </Section>
      <Section background="BLUE">
        <Row>
          <Column className="text-left sm:text-center space-y-3">
            <Text.H2>
              {supplementSection.snippetCollection?.items[0]?.name}
            </Text.H2>
            <Text.Body>
              {supplementSection.snippetCollection?.items[0]?.subtitle}
            </Text.Body>
          </Column>
        </Row>
        <Row>
          <Column>
            <RichText
              content={supplementSection.snippetCollection?.items[0]?.content}
            />
          </Column>
          {supplementSection.snippetCollection?.items[0]?.image?.url &&
            supplementSection.snippetCollection.items[0].image.width &&
            supplementSection.snippetCollection.items[0].image.height && (
              <Column className="order-first md:order-none">
                <Image
                  src={supplementSection.snippetCollection.items[0].image.url}
                  width={
                    supplementSection.snippetCollection.items[0].image.width
                  }
                  height={
                    supplementSection.snippetCollection.items[0].image.height
                  }
                  alt={
                    supplementSection.snippetCollection.items[0].image
                      .description ?? ''
                  }
                  className="max-w-sm md:max-w-full mx-auto !mb-8"
                />
              </Column>
            )}
        </Row>
      </Section>
      <CallToActionSection
        background="BLUE"
        planType="medicare"
        title="Interested in Blue Medicare Supplement?"
        subtitle="Get started today."
        quoteAction
        quoteActionLabel="See Medicare Plans"
        quoteActionSecondary
        agentAction
      />
      <CommonQuestions
        snippetGroup={commonQuestionsSection.snippetGroupCollection?.items[0]}
        inset
      />
      <CallToActionSection
        planType="medicare"
        title="Did we answer all your questions?"
        subtitle="Let us know what else we can answer for you about Medicare."
        agentAction
        agentActionLabel="Ask an Agent"
      />
      <FeaturedArticles
        background="BLUE"
        snippetGroup={
          articlesSection.snippetGroupCollection?.items[0] ?? undefined
        }
      />
      <ProductLogos
        snippetGroup={
          logosSection.snippetGroupCollection?.items[0] ?? undefined
        }
      />
      <CallToActionSection background="BLUE" planType="medicare" quoteAction />
      <Section>
        <Row inset>
          <Column className="[&_img]:block [&_img]:mx-auto break-words">
            <Text.H2 className="!text-xl">Footnotes</Text.H2>
            <RichText
              content={footnotesSection?.snippetCollection?.items[0]?.content}
            />
          </Column>
        </Row>
      </Section>
      <CallToActionSection
        planType="medicare"
        subtitle="Find out what medicare plans are best for you."
        agentAction
        agentActionLabel="Talk to an Agent About Medicare Plans"
      />
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  pageMetadata:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageMedicare>['data']
        >['pageCollection']
      >['items'][0]
    | undefined;
  heroData: Awaited<ReturnType<typeof heroData>>;
  medicareHeroData: GetServerPageProps<
    typeof getServerPageMedicareHeroSection
  >['data'];
  logosSection: GetServerPageProps<
    typeof getServerPageMedicareLogosSection
  >['data'];
  whySection: GetServerPageProps<
    typeof getServerPageMedicareWhySection
  >['data'];
  abcdSection: GetServerPageProps<
    typeof getServerPageMedicareAbcdSection
  >['data'];
  pathsSection: GetServerPageProps<
    typeof getServerPageMedicarePathsSection
  >['data'];
  supplementSection: GetServerPageProps<
    typeof getServerPageMedicareSupplementSection
  >['data'];
  commonQuestionsSection: GetServerPageProps<
    typeof getServerPageMedicareQuestionsSection
  >['data'];
  articlesSection: GetServerPageProps<
    typeof getServerPageMedicareArticlesSection
  >['data'];
  footnotesSection: GetServerPageProps<
    typeof getServerPageMedicarePlansFootnotes
  >['data'];
}> = async () => {
  const { data } = (await getServerPageMedicare({})).props;

  return {
    props: {
      pageMetadata: data.pageCollection?.items[0],
      heroData: await heroData(),
      medicareHeroData: (await getServerPageMedicareHeroSection({})).props.data,
      logosSection: (await getServerPageMedicareLogosSection({})).props.data,
      whySection: (await getServerPageMedicareWhySection({})).props.data,
      abcdSection: (await getServerPageMedicareAbcdSection({})).props.data,
      pathsSection: (await getServerPageMedicarePathsSection({})).props.data,
      supplementSection: (await getServerPageMedicareSupplementSection({}))
        .props.data,
      commonQuestionsSection: (await getServerPageMedicareQuestionsSection({}))
        .props.data,
      articlesSection: (await getServerPageMedicareArticlesSection({})).props
        .data,
      footnotesSection: (await getServerPageMedicarePlansFootnotes({})).props
        .data,
    },
  };
};
